<script lang="ts">
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"

  import type { OrgaViewFrameContext } from "../../../../context"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Table, {
    type TableFilterValues,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { getSortedTablePage } from "@kontextwerk/web-sdk/core/layout/tableSort"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiDelete, mdiPencil, mdiPlus, mdiRestore } from "@mdi/js"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import {
    deleteOrderType,
    fetchOrderTypes,
    restoreOrderTypeDefaults,
  } from "../../../../../../api/kehrblatt/order-types"
  import OrderTypeTableCell from "./_modules/order-type-table-cell"
  import OrderTypeOverlay from "./_modules/order-type-overlay"
  import {
    filterOrderTypeRows,
    orderTypeColumns,
    orderTypeFilter,
    toOrderTypeRows,
    type OrderTypeRow,
  } from "./_shared/_logic/table-model"
  import {
    SETTINGS_ACTION_TOOLTIP_CLASS,
    settingsActionTooltips,
  } from "./_logic/settingsActionTooltips"

  const { viewFrameContext } = $props<{ viewFrameContext: OrgaViewFrameContext }>()
  const organization = $derived(viewFrameContext.organization)
  const canEdit = $derived(viewFrameContext.permissions.allows("core.org_settings", "manage"))
  let orderTypes = $state<OrderType[]>([])
  let tableRefresh = $state(0)
  let refreshTable: (() => void | Promise<void>) | null = null
  const numberFormatter = new Intl.NumberFormat("de-DE")
  let totalCount = $state<number | null>(null)
  const titleWithCount = $derived.by(
    () => `Auftragstypen (${numberFormatter.format(totalCount ?? 0)})`,
  )

  const getRows: TableGetRows<TableFilterValues> = async (params) => {
    const { filters, limit, offset } = params
    const sort =
      (params as { sort?: Parameters<typeof getSortedTablePage<OrderTypeRow>>[0]["sort"] }).sort ??
      null

    if (!organization?.id) return { rows: [], total: 0 }

    const result = await fetchOrderTypes(organization.id)
    orderTypes = result ?? []
    const filtered = filterOrderTypeRows(toOrderTypeRows(orderTypes), filters)

    return getSortedTablePage({
      rows: filtered,
      columns: orderTypeColumns,
      sort,
      limit,
      offset,
    })
  }

  const upsertType = (updated: OrderType) => {
    const existingIndex = orderTypes.findIndex((entry) => entry.id === updated.id)

    if (existingIndex >= 0) {
      const clone = [...orderTypes]
      clone[existingIndex] = updated
      orderTypes = clone
    } else {
      orderTypes = [updated, ...orderTypes]
    }

    refreshTable?.()
  }

  const openCreateOverlay = () => {
    if (!organization?.id || !canEdit) return

    openOverlay({
      title: "Auftragstyp anlegen",
      content: OrderTypeOverlay,
      closeOnParentOpen: true,
      properties: {
        organizationId: organization.id,
        onSaved: (created: OrderType) => {
          upsertType(created)
        },
      },
    })
  }

  const restoreDefaults = async () => {
    if (!organization?.id || !canEdit) return

    await newApproval({
      title: "Defaults wiederherstellen",
      description:
        "Default-Auftragstypen werden ergänzt und auf die Standardwerte zurückgesetzt. Eigene Auftragstypen werden entfernt.",
      approvalLabel: "Wiederherstellen",
      declineLabel: "Abbrechen",
      approvalCallback: async () => {
        try {
          const result = await restoreOrderTypeDefaults(organization.id)
          refreshTable?.()
          newNotification({
            class: "success",
            html: `Defaults wiederhergestellt. Neu: ${result.created}, zurückgesetzt: ${result.updated}.`,
          })
        } catch (err) {
          console.error("order_type_defaults_restore_failed", err)
          newNotification({
            class: "error",
            html: "Defaults konnten nicht wiederhergestellt werden.",
          })
        }
      },
    })
  }

  const openEditOverlay = (orderType: OrderType) => {
    if (!organization?.id || !canEdit) return

    openOverlay({
      title: "Auftragstyp bearbeiten",
      content: OrderTypeOverlay,
      closeOnParentOpen: true,
      properties: {
        organizationId: organization.id,
        orderType,
        onSaved: (updated: OrderType) => {
          upsertType(updated)
        },
      },
    })
  }

  const removeOrderType = async (orderType: OrderType) => {
    if (!canEdit || orderType.is_protected) return

    await newApproval({
      title: "Auftragstyp löschen",
      description: `Auftragstyp "${orderType.internal_label}" wirklich löschen? Neue Aufträge können diesen Typ danach nicht mehr auswählen.`,
      approvalLabel: "Ja, löschen",
      declineLabel: "Abbrechen",
      approvalCallback: async () => {
        try {
          await deleteOrderType(orderType.id)
          orderTypes = orderTypes.filter((entry) => entry.id !== orderType.id)
          refreshTable?.()
          newNotification({ class: "success", html: "Auftragstyp wurde gelöscht." })
        } catch (err) {
          console.error("order_type_delete_failed", err)
          newNotification({ class: "error", html: "Auftragstyp konnte nicht gelöscht werden." })
        }
      },
    })
  }
</script>

<div class="section-header">
  <SectionTitle title={titleWithCount} brightBackground={true} helpModalTextId="orderTypes">
    {#snippet buttonSnippet()}
      <div class="section-actions">
        {#if canEdit}
          <CTA
            type="secondaryButton"
            content=""
            tooltipContent={settingsActionTooltips.restoreOrderTypeDefaults}
            tooltipClassName={SETTINGS_ACTION_TOOLTIP_CLASS}
            onClick={restoreDefaults}
          >
            {#snippet iconSnippet()}
              <Icon path={mdiRestore} />
            {/snippet}
          </CTA>
          <CTA
            type="primaryButton"
            content=""
            tooltipContent="Auftragstyp anlegen"
            onClick={openCreateOverlay}
          >
            {#snippet iconSnippet()}
              <Icon path={mdiPlus} />
            {/snippet}
          </CTA>
        {/if}
      </div>
    {/snippet}
  </SectionTitle>
</div>

<Table
  rowActions={{
    resolve: (row, rowIndex) =>
      buildTableRowActionSet(
        canEdit
          ? (((row) => {
              const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] = []

              const typeRow = row as OrderTypeRow

              actions.push({
                id: "action-0",
                label: "Auftragstyp bearbeiten",
                onClick: () => openEditOverlay(typeRow.orderType),
                icon: mdiPencil,
                tooltip: "Auftragstyp bearbeiten",
              })

              if (!typeRow.orderType.is_protected)
                actions.push({
                  id: "action-1",
                  label: "Löschen",
                  onClick: () => removeOrderType(typeRow.orderType),
                  icon: mdiDelete,
                  tooltip: settingsActionTooltips.deleteOrderType,
                  tone: "danger",
                })

              return actions
            })(row) ?? [])
          : [],
        { primaryId: "action-0", inlineIds: [] },
      ),
  }}
  columns={orderTypeColumns}
  {getRows}
  refreshKey={tableRefresh}
  theme="light"
  mode="table"
  showActions={canEdit}
  actionsHeaderLabel="Aktionen"
  urlStateId={organization?.id
    ? `kehrblatt-order-types-${organization.id}`
    : "kehrblatt-order-types"}
  filter={orderTypeFilter}
  onRowsLoaded={(result) => {
    totalCount = typeof result?.total === "number" ? result.total : (result?.rows?.length ?? 0)
  }}
  setRefreshFunction={(fn) => (refreshTable = fn)}
>
  {#snippet tableCell({ row, column })}
    {@const typeRow = row as OrderTypeRow}
    <OrderTypeTableCell row={typeRow} {column} />
  {/snippet}

  {#snippet empty()}
    <div class="empty-state">
      <p style="color: var(--text-invers-100)">Keine Auftragstypen vorhanden.</p>
    </div>
  {/snippet}
</Table>

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .section-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 0;
  }
</style>
